'use client';

import * as Sentry from '@sentry/nextjs';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { useShallow } from 'zustand/react/shallow';

import { MenuClientHideConditions, ServerLink, ServerMenu } from '../helpers';
import { MenuLinkClientWrapper } from './MenuLinkClientWrapper';
import { SportsClientLink } from './SportsClientLink';
import { getSportsHref } from '@/helpers/variables';
import useEstonia from '@/hooks/estonia';
import { useCommonStore } from '@/store';

import { ScMenu, ScStripe, ScStripeProps } from './styled';

export interface MenuProps {
  locale: string;
  links: ServerLink[];
  withStripe?: boolean;
  signOutBtn?: React.ReactElement | null;
  onClickMenuItem?: () => void;
  hostname: string;
}

export const Menu = ({
  locale,
  links,
  withStripe,
  signOutBtn,
  onClickMenuItem,
  hostname,
}: MenuProps) => {
  const currentPage = usePathname();
  const { images, tabBarMenu: tabBarMenuIcons } = useTheme();

  const { isMobileDevice, isAuthenticated } = useCommonStore(
    useShallow((state) => ({
      isMobileDevice: state.isMobileDevice,
      isAuthenticated: state.isAuthenticated,
    }))
  );

  const [stripe, setStripe] = useState<ScStripeProps | undefined>(undefined);

  const activeItemRef = useRef<HTMLLIElement>(null);

  const setPositionStripe = useCallback(() => {
    if (
      withStripe == null ||
      activeItemRef.current == null ||
      currentPage == null
    ) {
      setStripe(undefined);
      return;
    }

    const element = activeItemRef.current;
    const stripeWidth = element.clientWidth;
    const stripeLeft = element.offsetLeft + window.scrollX;

    setStripe({ stripeWidth, stripeLeft });
  }, [withStripe, setStripe, currentPage, activeItemRef]);

  useEffect(() => {
    setPositionStripe();
  }, [isAuthenticated, setPositionStripe, activeItemRef.current?.offsetWidth]);

  useEffect(() => {
    setPositionStripe();

    window.addEventListener('resize', setPositionStripe);
    return () => window.removeEventListener('resize', setPositionStripe);
  }, [setPositionStripe]);

  const arrowElement = useMemo(() => {
    if (!isMobileDevice) return null;
    return (
      <div className="arrow">
        <images.arrowUpGrayIcon.component />
      </div>
    );
  }, [images, isMobileDevice]);

  const getLoyaltyData = useCommonStore.use.getLoyaltyData();
  const isLoyaltyDataExists = getLoyaltyData();
  const { isEstonia } = useEstonia();

  const hideConditionResults = {
    [MenuClientHideConditions.NotAuthenticated]: !isAuthenticated,
    [MenuClientHideConditions.NoLoyaltyData]: !isLoyaltyDataExists,
    [MenuClientHideConditions.IsEstonia]: isEstonia,
  };

  const handleClickMenuItem = () => {
    if (onClickMenuItem) {
      onClickMenuItem();
    }
    Sentry.addBreadcrumb({
      category: 'menu',
      message: `menu item ${currentPage} clicked`,
      level: 'info',
    });
  };

  return (
    <ScMenu>
      {links.map(({ id, url, hideConditions, serverComponent }) => {
        const hotfixUrl =
          id === ServerMenu.Sports ? getSportsHref(hostname, locale) : url;

        return (
          <MenuLinkClientWrapper
            key={id}
            id={id}
            url={hotfixUrl}
            ref={
              hotfixUrl === currentPage ||
              (hotfixUrl === '/' && currentPage.slice(0, 5) === '/game')
                ? activeItemRef
                : null
            }
            hideConditions={hideConditions}
            hideConditionResults={hideConditionResults}
            onClick={handleClickMenuItem}
          >
            {id === ServerMenu.Sports ? (
              <SportsClientLink
                url={hotfixUrl}
                locale={locale}
                isMobileDevice={isMobileDevice}
                iconMobile={tabBarMenuIcons.sportsMobileMenuIcon.component}
              />
            ) : (
              serverComponent
            )}
          </MenuLinkClientWrapper>
        );
      })}
      {signOutBtn && (
        <li>
          {signOutBtn}
          {arrowElement}
        </li>
      )}
      {withStripe && stripe && (
        <ScStripe
          stripeWidth={stripe.stripeWidth}
          stripeLeft={stripe.stripeLeft}
        />
      )}
    </ScMenu>
  );
};
